<template>
  <div class="language-selector">
    <button 
      @click="toggleDropdown" 
      class="language-button"
      :aria-label="$t('language.select')"
    >
      <span class="language-flag">{{ currentFlag }}</span>
      <span class="language-code">{{ currentLanguage.toUpperCase() }}</span>
      <span class="language-arrow">▼</span>
    </button>
    
    <Transition name="dropdown">
      <div v-if="showDropdown" class="language-dropdown">
        <button 
          @click="setLanguage('de')" 
          class="language-option"
          :class="{ active: currentLanguage === 'de' }"
        >
          <span class="language-flag">🇩🇪</span>
          <span>{{ $t('language.german') }}</span>
          <span v-if="currentLanguage === 'de'" class="check">✓</span>
        </button>
        <button 
          @click="setLanguage('en')" 
          class="language-option"
          :class="{ active: currentLanguage === 'en' }"
        >
          <span class="language-flag">🇬🇧</span>
          <span>{{ $t('language.english') }}</span>
          <span v-if="currentLanguage === 'en'" class="check">✓</span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { syncDocumentLang } from '../utils/documentLang'

const { locale } = useI18n()
const showDropdown = ref(false)

const currentLanguage = computed(() => locale.value)
const currentFlag = computed(() => locale.value === 'de' ? '🇩🇪' : '🇬🇧')

function toggleDropdown() {
  showDropdown.value = !showDropdown.value
}

function setLanguage(lang) {
  locale.value = lang
  localStorage.setItem('language', lang)
  syncDocumentLang(lang)
  showDropdown.value = false
}

function closeOnClickOutside(event) {
  if (!event.target.closest('.language-selector')) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', closeOnClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', closeOnClickOutside)
})
</script>

<style scoped>
.language-selector {
  position: relative;
  display: inline-block;
}

.language-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.language-button:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: var(--accent-green, rgba(0, 255, 179, 0.3));
}

.language-flag {
  font-size: 1.1rem;
}

.language-code {
  font-weight: 600;
}

.language-arrow {
  font-size: 0.7rem;
  transition: transform 0.2s ease;
  opacity: 0.7;
}

.language-button:hover .language-arrow {
  opacity: 1;
}

.language-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--glass-bg);
  backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 8px;
  min-width: 160px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 10000;
}

.language-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.language-option:hover {
  background: rgba(255, 255, 255, 0.1);
}

.language-option.active {
  background: rgba(0, 255, 179, 0.1);
  color: var(--accent-green, #00ffb3);
}

.language-option .check {
  margin-left: auto;
  color: var(--accent-green, #00ffb3);
  font-weight: bold;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 768px) {
  .language-button {
    padding: 6px 10px;
    font-size: 0.85rem;
  }
  
  .language-dropdown {
    right: 0;
    min-width: 140px;
  }
}
</style>
